import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const PRIMARY_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const FALLBACK_API_URLS = [
  process.env.REACT_APP_API_URL_FALLBACK,
  'https://psych-backend.onrender.com/api',
  'https://psych-backend-n0tj.onrender.com/api'
].filter(Boolean);

const API_URLS = [...new Set([PRIMARY_API_URL, ...FALLBACK_API_URLS])];
const AUTH_REQUEST_TIMEOUT_MS = Number(process.env.REACT_APP_AUTH_REQUEST_TIMEOUT_MS) || 90000;
const AUTH_RETRY_DELAY_MS = 1500;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableNetworkError = (error) => {
  if (error.response) return false;

  return Boolean(error.request) || ['ECONNABORTED', 'ERR_NETWORK', 'ETIMEDOUT'].includes(error.code);
};

const requestWithFallback = async (method, path, data, config = {}) => {
  const {
    retryCount = 0,
    retryDelayMs = AUTH_RETRY_DELAY_MS,
    ...axiosConfig
  } = config;
  let lastError;

  for (const baseUrl of API_URLS) {
    for (let attempt = 0; attempt <= retryCount; attempt += 1) {
      try {
        return await axios({
          method,
          url: `${baseUrl}${path}`,
          data,
          timeout: AUTH_REQUEST_TIMEOUT_MS,
          ...axiosConfig
        });
      } catch (error) {
        lastError = error;
        if (!isRetryableNetworkError(error)) {
          throw error;
        }

        if (attempt < retryCount) {
          await sleep(retryDelayMs * (attempt + 1));
        }
      }
    }
  }

  throw lastError;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await requestWithFallback('get', '/auth/me', undefined, { retryCount: 1 });
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to load user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await requestWithFallback('post', '/auth/practitioner/login', {
      email,
      password
    }, { retryCount: 1 });
    
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const register = async (formData) => {
    const response = await requestWithFallback('post', '/auth/practitioner/register', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout,
      isAuthenticated: !!user,
      isApproved: user?.approvalStatus === 'approved'
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};